import moment from 'moment';

/**
 * Server time anchor.
 *
 * An answer composed while the service is down carries the date it was written on the applicant
 * device. Reading that date from the system clock would let an applicant move his clock back and
 * pretend he composed before the end of the session.
 *
 * The anchor keeps the last date given by the API together with the value of performance.now()
 * at that instant: elapsed time is then measured on the monotonic clock, which no clock change
 * can move. Falls back to the system clock when no API response has been seen yet, which in
 * practice never happens on the exam page (session access always answers first).
 */

let anchor = null;

function getMonotonicNow() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : null;
}

/*
 * Store the reference given by the server. Called on every API response carrying a server date.
 */
export function setAnchor(serverTime) {
    if(!serverTime) {
        return null;
    }
    const monotonicNow = getMonotonicNow();
    if(monotonicNow === null) {
        // No monotonic clock available (very old browser): keep the system clock offset instead
        anchor = {
            serverTime: moment.utc(serverTime),
            monotonic: null,
            systemTime: moment.utc(),
        };
        return anchor;
    }
    anchor = {
        serverTime: moment.utc(serverTime),
        monotonic: monotonicNow,
        systemTime: null,
    };
    return anchor;
}

/*
 * Current date, anchored on the last known server time
 */
export function getAnchoredNow() {
    if(!anchor) {
        return moment.utc();
    }
    if(anchor.monotonic !== null) {
        const monotonicNow = getMonotonicNow();
        return moment.utc(anchor.serverTime).add(Math.max(0, monotonicNow - anchor.monotonic), 'milliseconds');
    }
    return moment.utc(anchor.serverTime).add(Math.max(0, moment.utc().diff(anchor.systemTime)), 'milliseconds');
}

export function hasAnchor() {
    return !!anchor;
}

/*
 * Test purpose only
 */
export function clearAnchor() {
    anchor = null;
}
